import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin, Truck, Phone, Clock, Package, RefreshCw,
  ChevronDown, ChevronUp, Check, Navigation, User
} from 'lucide-react';
import { toast } from 'sonner';

import { getToken } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ZONES_CONFIG = {
  figeac:     { label: 'Figeac', color: 'var(--admin-teal)', emoji: '🏠', km: '0–10 km' },
  capdenac:   { label: 'Capdenac / Decazeville', color: 'var(--admin-gold)', emoji: '🅿️', km: '15–25 km' },
  cahors:     { label: 'Cahors', color: 'var(--admin-purple)', emoji: '🎓', km: '70–85 km' },
  saint_cere: { label: 'Saint-Céré', color: '#F4A7B2', emoji: '🥊', km: '40–55 km' },
  autre:      { label: 'Autre zone', color: '#9CA3AF', emoji: '📍', km: 'variable' },
};

const STATUTS_TERRAIN = [
  { key: 'pret_depart',     label: 'Prêt à partir',      color: '#3B82F6' },
  { key: 'en_route',        label: 'En route',           color: 'var(--admin-purple)' },
  { key: 'arrive',          label: 'Arrivé sur place',    color: 'var(--admin-amber)' },
  { key: 'installation',    label: 'Installation',       color: '#F97316' },
  { key: 'installe',        label: 'Installé ✓',         color: '#22C55E' },
  { key: 'retour_prevu',    label: 'Retour prévu',       color: 'var(--admin-gold)' },
  { key: 'retour_recupere', label: 'Retour récupéré',    color: 'var(--admin-gold)' },
];

const CHAUFFEURS = ['Guillaume', 'Jean', 'Marie', 'Paul', 'Non assigné'];

function ZoneBlock({ zone, ficheList, onAssignChauffeur, onStatutChange }) {
  const conf = ZONES_CONFIG[zone] || ZONES_CONFIG.autre;
  const [open, setOpen] = useState(true);

  return (
    <div className="rounded-2xl overflow-hidden mb-4" style={{ border: `1px solid ${conf.color}40` }}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 text-left"
        style={{ background: `${conf.color}08`, border: 'none', cursor: 'pointer' }}>
        <span className="text-xl">{conf.emoji}</span>
        <div className="flex-1">
          <p className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>
            {conf.label} <span className="text-xs font-normal" style={{ color: 'var(--admin-muted)' }}>({conf.km})</span>
          </p>
          <p className="text-xs" style={{ color: conf.color }}>{ficheList.length} livraison{ficheList.length > 1 ? 's' : ''}</p>
        </div>
        {open ? <ChevronUp size={16} style={{ color: 'var(--admin-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--admin-muted)' }} />}
      </button>

      {open && (
        <div className="p-3 space-y-3">
          {ficheList.map((fiche, idx) => (
            <div key={fiche.id} className="card-premium p-4">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs"
                  style={{ background: conf.color, color: 'var(--admin-text)' }}>
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{fiche.client_nom}</p>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--admin-teal-dim)', color: '#0B4B4E' }}>
                      {fiche.heure_livraison || '?h?'}
                    </span>
                  </div>
                  <p className="text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>
                    <MapPin size={10} className="inline mr-1" />{fiche.adresse || 'Adresse non renseignée'}
                  </p>
                  {fiche.client_telephone && (
                    <a href={`tel:${fiche.client_telephone}`} className="flex items-center gap-1 text-xs mb-1" style={{ color: 'var(--admin-teal)' }}>
                      <Phone size={10} /> {fiche.client_telephone}
                    </a>
                  )}
                  <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
                    <Package size={10} className="inline mr-1" />{fiche.items?.length || 0} article{(fiche.items?.length || 0) > 1 ? 's' : ''}
                  </p>

                  {/* Assigner chauffeur */}
                  <div className="flex items-center gap-2 mt-2">
                    <User size={12} style={{ color: 'var(--admin-muted)' }} />
                    <select
                      value={fiche.chauffeur || ''}
                      onChange={e => onAssignChauffeur(fiche.id, e.target.value)}
                      className="flex-1 px-2 py-1 rounded-lg text-xs"
                      style={{ border: '1px solid var(--admin-border)', background: 'var(--admin-bg)', color: 'var(--admin-text)', outline: 'none' }}>
                      <option value="">-- Chauffeur --</option>
                      {CHAUFFEURS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  {/* Statut terrain rapide */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {STATUTS_TERRAIN.slice(0, 4).map(s => (
                      <button key={s.key}
                        onClick={() => onStatutChange(fiche.id, s.key)}
                        className="px-2 py-0.5 rounded-lg text-xs font-semibold"
                        style={{
                          background: fiche.statut === s.key ? s.color : `${s.color}15`,
                          color: fiche.statut === s.key ? '#FFF' : s.color,
                          border: `1px solid ${s.color}40`,
                          cursor: 'pointer',
                        }}>
                        {s.label}
                      </button>
                    ))}
                  </div>

                  {/* Lien GPS */}
                  {fiche.adresse && (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fiche.adresse)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs mt-2 font-semibold"
                      style={{ color: 'var(--admin-teal)' }}>
                      <Navigation size={12} /> Ouvrir dans Maps
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminTournees() {
  const [tourneeData, setTourneeData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [fiches, setFiches] = useState({});

  const token = getToken();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/tournees/optimisees?date_tournee=${selectedDate}`, { headers });
      const data = await res.json();
      setTourneeData(data);
      // Construire map fiche_id => fiche pour les updates locaux
      const map = {};
      (data.zones || []).forEach(z => z.fiches.forEach(f => { map[f.id] = f; }));
      setFiches(map);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  // eslint-disable-next-line
  }, [selectedDate]);

  useEffect(() => { load(); }, [load]);

  const assignerChauffeur = async (ficheId, chauffeur) => {
    await fetch(`${BACKEND}/api/admin/tournees/assigner-chauffeur`, {
      method: 'POST', headers,
      body: JSON.stringify({ fiche_id: ficheId, chauffeur }),
    });
    setFiches(prev => ({ ...prev, [ficheId]: { ...prev[ficheId], chauffeur } }));
    toast.success(`Chauffeur assigné : ${chauffeur}`);
  };

  const changerStatut = async (ficheId, statut) => {
    await fetch(`${BACKEND}/api/admin/exploitation/fiche/${ficheId}`, {
      method: 'PATCH', headers, body: JSON.stringify({ statut }),
    });
    setFiches(prev => ({ ...prev, [ficheId]: { ...prev[ficheId], statut } }));
    toast.success('Statut mis à jour');
  };

  // Reconstruire les zones avec les fiches mises à jour
  const zones = (tourneeData?.zones || []).map(z => ({
    ...z,
    fiches: z.fiches.map(f => fiches[f.id] || f),
  }));

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--admin-teal-dim)' }}>
              <Truck size={20} style={{ color: 'var(--admin-teal)' }} />
            </div>
            <h1 className="text-2xl font-display font-bold" style={{ color: 'var(--admin-text)' }}>Tournées Optimisées</h1>
          </div>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Regroupement géographique automatique des livraisons</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            className="px-3 py-2 rounded-xl text-sm"
            style={{ border: '1px solid var(--admin-border)', background: '#FFF', color: 'var(--admin-text)', outline: 'none' }} />
          <button onClick={() => load()}
            className="p-2 rounded-xl" style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', cursor: 'pointer' }}>
            <RefreshCw size={14} style={{ color: 'var(--admin-muted)' }} />
          </button>
        </div>
      </div>

      {/* Stats */}
      {tourneeData && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="card-premium p-4 text-center">
            <p className="font-display text-2xl font-bold" style={{ color: 'var(--admin-teal)' }}>{tourneeData.total_stops}</p>
            <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Livraisons totales</p>
          </div>
          <div className="card-premium p-4 text-center">
            <p className="font-display text-2xl font-bold" style={{ color: 'var(--admin-gold)' }}>{tourneeData.total_zones}</p>
            <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Zones desservies</p>
          </div>
          <div className="card-premium p-4 text-center">
            <p className="font-display text-2xl font-bold" style={{ color: 'var(--admin-green)' }}>
              {zones.reduce((s, z) => s + (z.distance_estimee_km || 0), 0)} km
            </p>
            <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>Distance estimée</p>
          </div>
        </div>
      )}

      {/* Info optimisation */}
      <div className="p-3 rounded-2xl mb-5 flex items-start gap-2" style={{ background: 'rgba(93,188,191,0.06)', border: '1px solid var(--admin-teal-line)' }}>
        <Navigation size={14} style={{ color: 'var(--admin-teal)', flexShrink: 0, marginTop: 2 }} />
        <p className="text-xs" style={{ color: '#0B4B4E' }}>
          Les livraisons sont regroupées automatiquement par zone géographique. L'ordre dans chaque zone est optimisé par heure de livraison. Cliquez sur "Ouvrir dans Maps" pour la navigation GPS.
        </p>
      </div>

      {/* Tournées par zone */}
      {loading ? (
        [1,2].map(i => <div key={i} className="h-32 rounded-2xl animate-pulse mb-3" style={{ background: 'var(--border)' }} />)
      ) : zones.length === 0 ? (
        <div className="text-center py-12">
          <Truck size={36} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
          <p className="font-semibold text-sm" style={{ color: 'var(--admin-muted)' }}>Aucune livraison prévue</p>
          <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>à cette date. Essayez une autre date.</p>
        </div>
      ) : (
        zones.map(z => (
          <ZoneBlock
            key={z.zone}
            zone={z.zone}
            ficheList={z.fiches}
            onAssignChauffeur={assignerChauffeur}
            onStatutChange={changerStatut}
          />
        ))
      )}
    </div>
  );
}
